import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  Chip,
  Stack,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { appointmentTypesAPI } from '../services/api';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import ConfirmDialog from '../components/ConfirmDialog';
import RowActionsMenu from '../components/RowActionsMenu';
import {
  tablePageRootSx,
  tablePageHeaderStickySx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';
import { toArray } from '../utils/apiData';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import useFieldErrors from '../utils/useFieldErrors';
import { TableSkeletonRows } from '../components/LoadingSkeletons';

const emptyFormData = {
  name: '',
  description: '',
  default_duration_minutes: 60,
};

const AppointmentTypesPage = () => {
  const notify = useNotify();
  const fields = useFieldErrors();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState(emptyFormData);

  const loadTypes = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      // active: 'false' disables the backend's active-only filter, returning
      // inactive types too so they stay editable/reactivatable here.
      const response = await appointmentTypesAPI.getAll({ active: 'false' });
      const sorted = [...toArray(response.data, 'appointment types')].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '')
      );
      setTypes(sorted);
    } catch (err) {
      console.error('Error loading appointment types:', err);
      setError(err.response?.data?.error || 'Failed to load appointment types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  const handleOpenDialog = (type = null) => {
    fields.reset();
    if (type) {
      setEditingType(type);
      setFormData({
        name: type.name || '',
        description: type.description || '',
        default_duration_minutes: type.default_duration_minutes ?? 60,
      });
    } else {
      setEditingType(null);
      setFormData(emptyFormData);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingType(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    fields.clear(name);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!fields.validate({ name: !formData.name.trim() && 'Name is required' })) return;
    try {
      if (editingType) {
        await appointmentTypesAPI.update(editingType.id, formData);
      } else {
        await appointmentTypesAPI.create(formData);
      }
      handleCloseDialog();
      loadTypes();
    } catch (error) {
      console.error('Error saving appointment type:', error);
      notify.error(errorMessage(error, 'Error saving appointment type'));
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteClick = (type) => setDeleteTarget(type);
  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    try {
      await appointmentTypesAPI.delete(deleteTarget.id);
      setDeleteTarget(null);
      loadTypes();
    } catch (error) {
      console.error('Error deleting appointment type:', error);
      notify.error(errorMessage(error, 'Error deleting appointment type'));
    }
  };

  const handleToggleActive = async (type) => {
    try {
      await appointmentTypesAPI.update(type.id, { active: !type.active });
      loadTypes();
    } catch (error) {
      console.error('Error updating status:', error);
      notify.error(errorMessage(error, 'Failed to update status'));
    }
  };

  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'flex-start', sm: 'space-between' },
            alignItems: 'center',
            gap: { xs: 1.5, sm: 0 },
            mb: 3,
          }}
        >
          <Typography variant="h4">Appointment Types</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              minWidth: { xs: 0, sm: 'auto' },
              width: { xs: 32, sm: 'auto' },
              height: { xs: 32, sm: 'auto' },
              px: { xs: 0, sm: 2 },
              '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Add Appointment Type
            </Box>
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      <Box sx={tablePageScrollRegionSx}>
        <TableContainer component={Paper} sx={tablePageTableContainerSx}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Default Duration (min)</TableCell>
                <TableCell align="center">Active</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <TableSkeletonRows columns={5} />}
              {types.map((type) => (
                <TableRow key={type.id}>
                  <TableCell align="center" sx={actionsKabobCellSx}>
                    <RowActionsMenu
                      actions={[
                        { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenDialog(type) },
                        { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => handleDeleteClick(type), color: 'error' },
                      ]}
                    />
                  </TableCell>
                  <TableCell>{type.name}</TableCell>
                  <TableCell>{type.description || '-'}</TableCell>
                  <TableCell align="right">{type.default_duration_minutes ?? '-'}</TableCell>
                  <TableCell align="center">
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Switch
                        checked={Boolean(type.active)}
                        onChange={() => handleToggleActive(type)}
                        color="primary"
                      />
                      {!type.active && (
                        <Chip label="Inactive" size="small" color="warning" />
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && types.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No appointment types found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingType ? 'Edit Appointment Type' : 'Add New Appointment Type'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              size="small"
              name="name"
              label="Name"
              value={formData.name}
              onChange={handleInputChange}
              {...fields.props('name')}
              fullWidth
              required
            />
            <TextField
              size="small"
              name="description"
              label="Description"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={3}
              fullWidth
            />
            <TextField
              size="small"
              name="default_duration_minutes"
              label="Default Duration (minutes)"
              type="number"
              value={formData.default_duration_minutes}
              onChange={handleInputChange}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingType ? 'Save Changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Appointment Type"
        message={`Permanently delete appointment type "${deleteTarget?.name}"? This cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Box>
  );
};

export default AppointmentTypesPage;
